import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Typography } from "@pondigitalsolutions/pon-component-library";

import { Layout } from "@components/Layout";
import { useCurrentLang } from "@hooks/useCurrentLang";
import { languageDutch, languageEnglish } from "../i18n";
import { LanguageSwitch } from "@components/ControlCenter/LanguageSwitch";
import { ThemeSwitch } from "@components/ControlCenter/ThemeSwitch";
import { useUserContext } from "@hooks/useUserContext";
import { Alert } from "@components/Alert";
import { useOktaUser } from "@hooks/useOktaUser";
import { Loader } from "@components/Loader";
import { Toggle } from "@components/Toggle";
import { useUpdatePreference } from "@hooks/useUpdatePreference";
import { AutoTranslateLanguageDropdown } from "@components/ControlCenter/AutoTranslateLanguageDropdown";

const DIVIDER_STYLES = "border-t border-tertiary-100 dark:border-white/20";

export const SettingsPage = () => {
  const { lang } = useParams();
  const { t } = useTranslation();
  const currentLang = useCurrentLang(lang);

  const pageSlug = "settings";
  const translatedSlug =
    currentLang === languageDutch
      ? `/${languageEnglish}/${pageSlug}`
      : `/${pageSlug}`;

  const { user, isLoading } = useUserContext();
  const { data: oktaUserData } = useOktaUser();
  const { mutate: updatePreferences } = useUpdatePreference();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader />
      </div>
    );
  }

  // If there is no user, then something went wrong
  if (!user) {
    // show error message
    return (
      <Alert
        labelText={t("ControlCenter.error.label")}
        text={t("ControlCenter.error.message")}
        variant="error"
        className="min-w-96"
        textClassName="text-tertiary-100"
      />
    );
  }

  const {
    themePreference: currentTheme,
    languagePreference,
    autoTranslateEnabled,
    autoTranslateLanguage,
  } = user;

  return (
    <Layout translatedSlug={translatedSlug}>
      <div className="pt-12 before:absolute before:top-0 before:right-0 before:left-0 before:block before:h-52 before:bg-secondary-900 before:content-[''] dark:before:bg-secondary-950">
        <div
          className={twMerge(
            "relative mx-auto mb-24 flex w-full max-w-4xl flex-col gap-8 rounded-3xl bg-tertiary-50 px-12 py-8 dark:bg-secondary-900"
          )}
        >
          {/* Image */}
          {oktaUserData?.firstName && (
            <div data-testid="settings-avatar" className="flex justify-center">
              <div className="flex aspect-square h-18 w-18 items-center justify-center rounded-full bg-tertiary-100 lg:h-42 lg:w-42 dark:bg-white/20">
                <Typography
                  size="2xl"
                  className="leading-[130%] font-bold text-tertiary-300 lg:text-5xl dark:text-tertiary-200"
                >
                  {oktaUserData.firstName.charAt(0)}
                </Typography>
              </div>
            </div>
          )}

          {/* Language preference */}
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-3">
              <Typography
                as="h2"
                size="2xl"
                className="text-secondary-700 dark:text-white"
              >
                {t("Settings.language")}
              </Typography>
              <Typography className="font-normal text-tertiary-900 dark:text-white">
                {t("Settings.languageDescription")}
              </Typography>
            </div>
            <LanguageSwitch
              translatedSlug={translatedSlug}
              languagePreference={languagePreference}
              buttonVariant="dark"
              className="w-56"
            />
            <div className="flex gap-4">
              <Toggle
                checked={autoTranslateEnabled}
                onChange={(checked) =>
                  updatePreferences({ autoTranslateEnabled: checked })
                }
                ariaLabel={t("Settings.autoTranslate")}
                data-testid="auto-translate-toggle"
              />
              <div className="flex flex-col gap-1">
                <Typography className="leading-4 font-normal text-tertiary-900 dark:text-tertiary-100">
                  {t("Settings.autoTranslate")}
                </Typography>
                <Typography
                  size="sm"
                  className="leading-4 font-normal text-tertiary-500 dark:text-tertiary-300"
                >
                  {t("Settings.autoTranslateDescription")}
                </Typography>
              </div>
            </div>

            <AutoTranslateLanguageDropdown
              enabled={autoTranslateEnabled}
              language={autoTranslateLanguage}
            />
          </div>

          <div className={DIVIDER_STYLES} />

          {/* Appearance */}
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-3">
              <Typography
                as="h2"
                size="2xl"
                className="text-secondary-700 dark:text-white"
              >
                {t("Settings.appearance")}
              </Typography>
              <Typography className="font-normal text-tertiary-900 dark:text-white">
                {t("Settings.appearanceDescription")}
              </Typography>
            </div>
            <ThemeSwitch
              currentTheme={currentTheme}
              buttonVariant="dark"
              className="lg:w-85"
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};
